import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePlaces } from '@/hooks/usePlaces';
import { useFavorites } from '@/hooks/useFavorites';
import { PlaceCard } from '@/components/PlaceCard';
import { SearchIcon } from '@/components/Icons';
import { LocalityFilterRow } from '@/components/LocalityFilterRow';
import { CategoryChipRow } from '@/components/CategoryChipRow';
import { parsePlaceFilterCategory, type PlaceFilterCategory } from '@/lib/interests';
import {
  groupByLocality,
  LOCALITY_BLURBS,
  LOCALITY_LABELS,
  parseLocalityFilter,
  type LocalityFilter,
  type LocalityGroup,
} from '@shared/locality';
import { placesWithDistance } from '@shared/places';
import { parseOutsideSort, sortOutsidePlaces, type OutsideSort } from '@/lib/outsidePlaceSort';

function initialFilters(search: string): {
  locality: LocalityFilter;
  category: PlaceFilterCategory;
  outsideSort: OutsideSort;
} {
  const q = new URLSearchParams(search);
  return {
    locality: parseLocalityFilter(q.get('locality')),
    category: parsePlaceFilterCategory(q.get('category')),
    outsideSort: parseOutsideSort(q.get('sort')),
  };
}

export function PlacesPage() {
  const { places: fetchedPlaces, loading } = usePlaces();
  const places = fetchedPlaces.length >= 15 ? fetchedPlaces : placesWithDistance();
  const { isFavorite, toggle } = useFavorites();
  const { search } = useLocation();
  const navigate = useNavigate();
  const initial = useMemo(() => initialFilters(search), [search]);
  const [category, setCategory] = useState(initial.category);
  const [locality, setLocality] = useState<LocalityFilter>(initial.locality);
  const [outsideSort, setOutsideSort] = useState<OutsideSort>(initial.outsideSort);
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    setLocality(initial.locality);
    setCategory(initial.category);
    setOutsideSort(initial.outsideSort);
  }, [initial]);

  useEffect(() => {
    const q = new URLSearchParams();
    if (locality !== 'both') q.set('locality', locality);
    if (category !== 'all') q.set('category', category);
    if (outsideSort === 'az') q.set('sort', 'az');
    const nextSearch = q.toString();
    if (nextSearch !== search.replace(/^\?/, '')) {
      navigate({ pathname: '/places', search: nextSearch }, { replace: true });
    }
  }, [locality, category, outsideSort, navigate, search]);

  const filtered = useMemo(() => {
    let list = places;
    if (category !== 'all') list = list.filter((p) => p.category === category);
    if (filterText.trim()) {
      const q = filterText.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q) || p.tags.some((t) => t.toLowerCase().includes(q)));
    }
    return [...list].sort((a, b) => a.distance_km - b.distance_km);
  }, [places, category, filterText]);

  const grouped = useMemo(() => groupByLocality(filtered), [filtered]);
  const outsideSorted = useMemo(
    () => sortOutsidePlaces(grouped.outside, outsideSort),
    [grouped.outside, outsideSort],
  );
  const visibleGroups: LocalityGroup[] = locality === 'both' ? ['inside', 'outside'] : [locality];
  const totalShown = visibleGroups.reduce((n, g) => n + grouped[g].length, 0);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Places</h1>
        <p className="page-subtitle">Beaches, food, and errands near Rocha Brava</p>
      </div>

      <div className="places-sticky">
        <div className="search-box" style={{ marginBottom: 8 }}>
          <SearchIcon />
          <label htmlFor="places-filter" className="visually-hidden">
            Filter places
          </label>
          <input
            id="places-filter"
            type="search"
            placeholder="Filter places"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>
        <LocalityFilterRow
          value={locality}
          onChange={setLocality}
          loading={loading}
          counts={{ inside: grouped.inside.length, outside: grouped.outside.length }}
        />
        <CategoryChipRow value={category} onChange={setCategory} />
      </div>

      {loading && places.length === 0 && (
        <div className="stack-gap">
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton" style={{ height: 140 }} />
          ))}
        </div>
      )}
      {!loading && totalShown === 0 && <p className="empty-state">No places match yet.</p>}
      {totalShown > 0 &&
        visibleGroups.map((group) => {
          const items = group === 'outside' ? outsideSorted : grouped[group];
          if (items.length === 0) return null;
          return (
            <section key={group} className="locality-section" aria-labelledby={`locality-${group}`}>
              <div className="locality-header">
                <h2 id={`locality-${group}`} className="locality-header__title">
                  {LOCALITY_LABELS[group]}
                  <span className="locality-header__count">{items.length}</span>
                </h2>
                <p className="locality-header__blurb">{LOCALITY_BLURBS[group]}</p>
              </div>
              {group === 'outside' && (
                <div className="chip-row" role="group" aria-label="Sort">
                  <button
                    type="button"
                    className={`interest-chip ${outsideSort === 'distance' ? 'interest-chip--on' : ''}`}
                    aria-pressed={outsideSort === 'distance'}
                    onClick={() => setOutsideSort('distance')}
                  >
                    Distance
                  </button>
                  <button
                    type="button"
                    className={`interest-chip ${outsideSort === 'az' ? 'interest-chip--on' : ''}`}
                    aria-pressed={outsideSort === 'az'}
                    onClick={() => setOutsideSort('az')}
                  >
                    A–Z
                  </button>
                </div>
              )}
              {items.map((p) => (
                <PlaceCard key={p.id} place={p} isFavorite={isFavorite(p.id)} onToggleFavorite={toggle} />
              ))}
            </section>
          );
        })}
    </div>
  );
}
